import { CheckCircle02Icon, ChevronRightIcon } from '@forwardflow/icons'
import {
   BASE_SEAT_COST,
   ADDITIONAL_SEAT_COST,
} from '@forwardflow/constants/onboarding.constants'
import { OnboardingV2Service } from '@forwardflow/services/onboardingV2.service'
import { useAppStore } from '@forwardflow/store/appStore'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { Button, ContentCard, Stack } from '@forwardflow/ui-v2'
import { Grid, NumberInput, Slider } from '@mantine/core'
import { useState, useMemo, useEffect } from 'react'
import { useSubscriptionPackageLists } from '@forwardflow/hooks/customHooks'
import cn from 'classnames'
import { isPaymentPricingReady } from '../payment-pricing'
import { DEV_DUMMY, isFreeFlow, useDevTools } from '../dev/devTools'

const MAX_SEATS = 50
const TEAM_MIN_SEATS = 2

type TPlanId = 'solo' | 'team'

const PLANS: { id: TPlanId; name: string; price: string; per: string; desc: string }[] = [
   {
      id: 'solo',
      name: 'Solo Agent',
      price: `$${BASE_SEAT_COST}`,
      per: '/month',
      desc: 'One user. Calls, texts and appointment setting for your own leads.',
   },
   {
      id: 'team',
      name: 'Team',
      price: `$${BASE_SEAT_COST + ADDITIONAL_SEAT_COST}`,
      per: '/month for 2 users',
      desc: `$${ADDITIONAL_SEAT_COST} for each additional user. Leads are routed to your agents.`,
   },
]

/**
 * Plan step: pick Solo or Team; Team shows a slider for the user count.
 * Lead budgets keep their stored defaults and can be changed from billing.
 */
export const PricingStepV3 = () => {
   const { onboardingData, getNextStep, setOrganizationId, setPricingData } =
      useOnboardingStoreV2()
   const { setCurrentOrganization } = useAppStore()
   const { email, isSubscriptionActivated, pricingData } = onboardingData
   const [isOnboardingStarting, setIsOnboardingStarting] = useState(false)

   const { data: subscriptionPackages } = useSubscriptionPackageLists({
      page: 1,
      limit: 10,
   })

   useEffect(() => {
      if (subscriptionPackages) {
         const selectedTier = subscriptionPackages?.pricingTiers?.[0]
         if (selectedTier && !pricingData?.selectedTierId) {
            setPricingData({ ...pricingData, selectedTierId: selectedTier.id })
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [subscriptionPackages])

   const seatsCount = pricingData?.seatsCount ?? 1
   const plan: TPlanId = seatsCount > 1 ? 'team' : 'solo'
   const maxSpend = pricingData?.maxSpend ?? 150
   const newLeadBudget = pricingData?.newLeadBudget ?? 93
   const reactivateLeadBudget = pricingData?.reactivateLeadBudget ?? 57

   const seatCost = useMemo(
      () => BASE_SEAT_COST + (seatsCount - 1) * ADDITIONAL_SEAT_COST,
      [seatsCount]
   )

   const handleSeatsChange = (value: number) => {
      const seats = Math.min(MAX_SEATS, Math.max(1, Math.round(value || 1)))
      setPricingData({ ...pricingData, seatsCount: seats })
   }

   const handlePlanChange = (next: TPlanId) => {
      if (next === 'solo') handleSeatsChange(1)
      else handleSeatsChange(Math.max(TEAM_MIN_SEATS, seatsCount))
   }

   const confirmedPricing = {
      ...pricingData,
      seatsCount,
      newLeadBudget,
      reactivateLeadBudget,
      maxSpend,
   }

   const handleStartOnboarding = async () => {
      try {
         // Free flow (dev tools): keep the shown values and move on; the
         // sandbox record has no organization to create.
         if (isFreeFlow()) {
            setPricingData(confirmedPricing)
            getNextStep()
            return
         }
         if (!email) return
         // Persist the displayed defaults on confirmation, not just values the
         // user edited. Otherwise an untouched seat count never reaches payment.
         if (!isPaymentPricingReady(confirmedPricing)) return
         setPricingData(confirmedPricing)
         setIsOnboardingStarting(true)
         const data = await OnboardingV2Service.startOnboarding({ email })
         const organizationId = data?.data?.id
         if (data?.success && organizationId) {
            setCurrentOrganization(organizationId)
            setOrganizationId(organizationId)
            getNextStep()
         }
      } catch (error) {
         console.error(error)
      } finally {
         setIsOnboardingStarting(false)
      }
   }

   // Dev tools (hidden bar): a three-user team, or confirm the plan.
   useDevTools({
      fill: () => handleSeatsChange(DEV_DUMMY.seats),
      skip: () => {
         void handleStartOnboarding()
         return true
      },
   })

   return (
      <Stack gap={32}>
         <ContentCard className="space-y-2">
            <p className="text-white font-medium">
               Welcome to Forward Flow! Pick the plan that fits
            </p>
            <p className="text-white/70 text-sm font-medium">
               You can switch plans or add users any time from your account
               settings.
            </p>
         </ContentCard>

         {/* Plan */}
         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PLANS.map((p) => {
               const selected = plan === p.id
               return (
                  <button
                     key={p.id}
                     type="button"
                     role="radio"
                     aria-checked={selected}
                     onClick={() => handlePlanChange(p.id)}
                     className={cn(
                        'text-left rounded-2xl border px-6 py-5 transition-all',
                        selected
                           ? 'border-[#9F7BFF] bg-[rgba(159,123,255,0.10)]'
                           : 'border-[#3f3e47] bg-white/[0.02] hover:border-white/30'
                     )}
                  >
                     <div className="flex items-start justify-between gap-3">
                        <div>
                           <p className="text-white font-semibold">{p.name}</p>
                           <p className="mt-1">
                              <span className="text-white font-semibold text-2xl">
                                 {p.price}
                              </span>
                              <span className="text-white/50 text-sm"> {p.per}</span>
                           </p>
                        </div>
                        <span
                           className={cn(
                              'mt-0.5 transition-opacity',
                              selected ? 'opacity-100' : 'opacity-0'
                           )}
                        >
                           <CheckCircle02Icon width={22} height={22} color="#A684FF" />
                        </span>
                     </div>
                     <p className="text-[#99A1AF] text-sm mt-3">{p.desc}</p>
                  </button>
               )
            })}
         </div>

         {/* Team size, only for Team */}
         {plan === 'team' && (
            <ContentCard
               className="!space-y-4"
               initial={{ opacity: 0, y: 20 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.25 }}
            >
               <Stack gap={4}>
                  <p className="text-white font-medium">How many users?</p>
                  <p className="text-[#99A1AF] text-sm">
                     ${BASE_SEAT_COST} for the first user + ${ADDITIONAL_SEAT_COST}{' '}
                     for each additional user. You invite them on the next steps.
                  </p>
               </Stack>
               <Grid className="items-center">
                  <Grid.Col span={{ base: 12, md: 8 }}>
                     <div className="bg-white/[0.02] rounded-lg p-4">
                        <Grid className="items-center">
                           <Grid.Col span={4}>
                              <NumberInput
                                 min={TEAM_MIN_SEATS}
                                 max={MAX_SEATS}
                                 value={seatsCount}
                                 classNames={{
                                    input: 'border border-[#B17AFF]/[0.5] rounded-lg p-2',
                                    section: 'w-20',
                                 }}
                                 onChange={(value) =>
                                    handleSeatsChange(
                                       Math.max(TEAM_MIN_SEATS, Number(value))
                                    )
                                 }
                                 rightSection={
                                    <span className="text-white/60 text-xs">
                                       Users
                                    </span>
                                 }
                              />
                           </Grid.Col>
                           <Grid.Col span={8} className="flex flex-col justify-center">
                              <Slider
                                 min={TEAM_MIN_SEATS}
                                 max={MAX_SEATS}
                                 value={seatsCount}
                                 classNames={{
                                    track: '!bg-[#D9D9D9]/10 !h-2 rounded-full',
                                    bar: '!bg-gradient-to-r !from-blue-500 !to-purple-500 !h-2',
                                    thumb: '!bg-white !w-4 !h-4 !rounded-full !border-0',
                                 }}
                                 label={(value) => `${value} users`}
                                 onChange={(value) => handleSeatsChange(value)}
                              />
                           </Grid.Col>
                        </Grid>
                     </div>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                     <div
                        className="rounded-xl p-4 px-8 border border-[#EB69C2]/[0.5] text-right"
                        style={{
                           background:
                              'linear-gradient(111deg, rgba(159, 123, 255, 0.10) 1.32%, rgba(235, 105, 194, 0.10) 96.08%)',
                        }}
                     >
                        <p className="text-white/50 text-sm font-medium">
                           {seatsCount} users
                        </p>
                        <p className="text-white font-semibold text-lg">
                           ${seatCost}/month
                        </p>
                     </div>
                  </Grid.Col>
               </Grid>
            </ContentCard>
         )}

         <Button
            className="w-full"
            onClick={handleStartOnboarding}
            loading={isOnboardingStarting}
            disabled={!isFreeFlow() && !isPaymentPricingReady(confirmedPricing)}
         >
            {isSubscriptionActivated
               ? 'Continue'
               : `Continue with ${plan === 'solo' ? 'Solo Agent' : 'Team'} · $${seatCost}/month`}{' '}
            <ChevronRightIcon width={18} />
         </Button>
      </Stack>
   )
}
